import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { FaSpinner, FaTimes } from "react-icons/fa";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { stampPdf } from "./services/pdfSign";
import { uploadSignedCopy, getProjectFileBytes } from "./services/projectDetailService";

// 24/10/2026 (Bartel): een pdf uit voorafDocs (bv. het RAMI-mandaat) moet in ELKE browser ondertekend
// kunnen worden, op de pc en op de gsm, zonder Acrobat of een pdf-viewer die tekenen toelaat. Daarom
// gebeurt het hier in de app: de pagina's worden met pdf.js getoond, de werker tekent zijn handtekening
// (muis of vinger), tikt op het document waar ze moet komen, kan er tekst bij plaatsen (naam, datum,
// uren in de prestatietabel) en verstuurt het resultaat. pdf-lib (pdfSign.js) plakt alles in de pdf en
// het bestand komt via de bestaande route (signed_upload_link) als "<naam>-ondertekend.pdf" in de
// projectmap zelf (sinds 26/10/2026 niet meer in voorafDocs, op vraag van Bartel).
// De pdf.js-worker staat als kopie in public/ (zelfde versie als pdfjs-dist in package.json), zodat
// de bundel er niet van afhangt.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const SIGNATURE_WIDTH = 0.28;   // breedte van een geplaatste handtekening, als deel van de paginabreedte
const TEXT_HEIGHT_PT = 13;      // hoogte van geplaatste tekst in pdf-punten (ongeveer 10 pt letter)
const MAX_PAGE_WIDTH = 900;

const base64ToBytes = (content) => {
    const bin = atob(content || "");
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
};

// Knipt de witruimte rond de handtekening weg en geeft een png + verhouding hoogte/breedte.
const trimCanvasToPng = (canvas) => {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (data[(y * width + x) * 4 + 3] > 0) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }
    if (maxX < 0) return null;
    const pad = 6;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
    const out = document.createElement("canvas");
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out.getContext("2d").drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return { png: out.toDataURL("image/png"), ratio: out.height / out.width };
};

// Tekent een stukje tekst naar een png (werkt ook voor Cyrillisch, geen lettertype in de pdf nodig).
const textToPng = (text) => {
    const scale = 4;
    const fontPx = 10 * scale;
    const canvas = document.createElement("canvas");
    let ctx = canvas.getContext("2d");
    ctx.font = `${fontPx}px Helvetica, Arial, sans-serif`;
    const measured = ctx.measureText(text).width;
    canvas.width = Math.ceil(measured + fontPx * 0.4);
    canvas.height = Math.ceil(fontPx * 1.3);
    ctx = canvas.getContext("2d");
    ctx.font = `${fontPx}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = "#000";
    ctx.textBaseline = "middle";
    ctx.fillText(text, fontPx * 0.2, canvas.height / 2);
    return { png: canvas.toDataURL("image/png"), ratio: canvas.height / canvas.width };
};

// Het tekenvlak voor de handtekening: muis, vinger of pen, in elke browser (pointer events).
const SignaturePad = ({ padRef, onChange }) => {
    const drawing = useRef(false);
    const last = useRef(null);

    useEffect(() => {
        const canvas = padRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0b2a6f";
        ctx.fillStyle = "#0b2a6f";
    }, [padRef]);

    const pos = (e) => {
        const rect = padRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const down = (e) => {
        e.preventDefault();
        padRef.current.setPointerCapture(e.pointerId);
        drawing.current = true;
        last.current = pos(e);
        const ctx = padRef.current.getContext("2d");
        ctx.beginPath();
        ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
        ctx.fill();
        onChange(true);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const p = pos(e);
        const ctx = padRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
    };
    const up = () => { drawing.current = false; };

    return (
        <canvas
            ref={padRef}
            className="sign-pad"
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onPointerLeave={up}
        />
    );
};

// Een geplaatste handtekening of tekst: te verslepen, te verwijderen met het kruisje.
const PlacedItem = ({ item, pageSize, onMove, onRemove }) => {
    const start = useRef(null);
    const hFrac = item.w * item.ratio * (pageSize.w / pageSize.h);

    const down = (e) => {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const rect = e.currentTarget.parentElement.getBoundingClientRect();
        start.current = { px: e.clientX, py: e.clientY, x: item.x, y: item.y, rect };
    };
    const move = (e) => {
        if (!start.current) return;
        const { px, py, x, y, rect } = start.current;
        onMove(item.id, x + (e.clientX - px) / rect.width, y + (e.clientY - py) / rect.height);
    };
    const up = () => { start.current = null; };

    return (
        <div
            className="sign-item"
            style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%`, width: `${item.w * 100}%`, height: `${hFrac * 100}%` }}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            onClick={(e) => e.stopPropagation()}
        >
            <img src={item.png} alt="" draggable={false} />
            <button
                type="button"
                className="sign-item-remove"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                aria-label="remove"
            >
                <FaTimes />
            </button>
        </div>
    );
};

// Eén pagina van de pdf, getekend door pdf.js, met daarover de geplaatste items.
const PdfPage = ({ pdf, index, width, placements, pageSize, onPlace, onMove, onRemove, armed }) => {
    const canvasRef = useRef(null);
    const renderTask = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const page = await pdf.getPage(index + 1);
            if (cancelled) return;
            const base = page.getViewport({ scale: 1 });
            const scale = width / base.width;
            const dpr = window.devicePixelRatio || 1;
            const viewport = page.getViewport({ scale: scale * dpr });
            const canvas = canvasRef.current;
            if (!canvas) return;
            canvas.width = Math.round(viewport.width);
            canvas.height = Math.round(viewport.height);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${Math.round(viewport.height / dpr)}px`;
            if (renderTask.current) renderTask.current.cancel();
            renderTask.current = page.render({ canvasContext: canvas.getContext("2d"), viewport });
            try { await renderTask.current.promise; } catch (e) { /* geannuleerd bij herrenderen */ }
        })();
        return () => { cancelled = true; };
    }, [pdf, index, width]);

    const heightPx = width * (pageSize.h / pageSize.w);

    const onClick = (e) => {
        if (!armed) return;
        const rect = e.currentTarget.getBoundingClientRect();
        onPlace(index, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
    };

    return (
        <div className={`sign-page${armed ? " sign-page-armed" : ""}`} style={{ width, height: heightPx }} onClick={onClick}>
            <canvas ref={canvasRef} />
            {placements.map((item) => (
                <PlacedItem key={item.id} item={item} pageSize={pageSize} onMove={onMove} onRemove={onRemove} />
            ))}
        </div>
    );
};

const SignPdfDialog = ({ file, projectId, onClose, onDone }) => {
    const [pdf, setPdf] = useState(null);
    const [pageSizes, setPageSizes] = useState([]);
    const [loadError, setLoadError] = useState(false);
    const [pageWidth, setPageWidth] = useState(600);
    const [placements, setPlacements] = useState([]);
    const [pending, setPending] = useState(null);   // { kind: "signature" | "text", png, ratio }
    const [hasSignature, setHasSignature] = useState(false);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);       // "nothing" | "upload"
    const [done, setDone] = useState(false);
    const padRef = useRef(null);
    const pagesRef = useRef(null);
    const nextId = useRef(1);
    const originalBytes = useRef(null);

    // Pagina's laden.
    useEffect(() => {
        let task = null;
        let cancelled = false;
        (async () => {
            try {
                // 27/10/2026 (Bartel, "voorafDocs sneller"): de lijst bevat geen inhoud meer; de pdf-bytes
                // komen via de tijdelijke Dropbox-link (of de backend als uitweg). Oude lijsten met
                // base64-inhoud blijven werken. pdf.js krijgt een kopie, het origineel dient om te stempelen.
                const bytes = file.content ? base64ToBytes(file.content) : await getProjectFileBytes(projectId, file.path);
                if (cancelled) return;
                originalBytes.current = bytes;
                task = pdfjsLib.getDocument({ data: bytes.slice() });
                const doc = await task.promise;
                if (cancelled) return;
                const sizes = [];
                for (let i = 1; i <= doc.numPages; i++) {
                    const vp = (await doc.getPage(i)).getViewport({ scale: 1 });
                    sizes.push({ w: vp.width, h: vp.height });
                }
                if (cancelled) return;
                setPageSizes(sizes);
                setPdf(doc);
            } catch (e) {
                console.error("Error loading pdf for signing:", e);
                if (!cancelled) setLoadError(true);
            }
        })();
        return () => { cancelled = true; if (task) task.destroy(); };
    }, [file, projectId]);

    // Paginabreedte volgt de beschikbare ruimte; de pagina eronder scrolt intussen niet mee.
    useEffect(() => {
        const measure = () => {
            const el = pagesRef.current;
            if (el) setPageWidth(Math.max(280, Math.min(MAX_PAGE_WIDTH, el.clientWidth - 16)));
        };
        measure();
        window.addEventListener("resize", measure);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("resize", measure);
            document.body.style.overflow = prev;
        };
    }, []);

    const armSignature = () => {
        const trimmed = padRef.current && trimCanvasToPng(padRef.current);
        if (!trimmed) return;
        setPending({ kind: "signature", ...trimmed });
        setError(null);
    };
    const armText = () => {
        const value = text.trim();
        if (!value) return;
        setPending({ kind: "text", ...textToPng(value) });
        setError(null);
    };
    const clearPad = () => {
        const canvas = padRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        setHasSignature(false);
        if (pending && pending.kind === "signature") setPending(null);
    };

    const place = useCallback((pageIndex, fx, fy) => {
        if (!pending) return;
        const size = pageSizes[pageIndex];
        const w = pending.kind === "signature"
            ? SIGNATURE_WIDTH
            : (TEXT_HEIGHT_PT / pending.ratio) / size.w;
        const h = w * pending.ratio * (size.w / size.h);
        const x = Math.min(Math.max(0, fx - w / 2), 1 - w);
        const y = Math.min(Math.max(0, fy - h / 2), 1 - h);
        setPlacements((list) => [
            ...list,
            { id: nextId.current++, page: pageIndex, x, y, w, ratio: pending.ratio, png: pending.png, kind: pending.kind },
        ]);
        setPending(null);
    }, [pending, pageSizes]);

    const movePlacement = useCallback((id, x, y) => {
        setPlacements((list) => list.map((p) => {
            if (p.id !== id) return p;
            const size = pageSizes[p.page];
            const h = p.w * p.ratio * (size.w / size.h);
            return { ...p, x: Math.min(Math.max(0, x), 1 - p.w), y: Math.min(Math.max(0, y), 1 - h) };
        }));
    }, [pageSizes]);

    const removePlacement = useCallback((id) => {
        setPlacements((list) => list.filter((p) => p.id !== id));
    }, []);

    const send = async () => {
        if (!placements.some((p) => p.kind === "signature")) {
            setError("nothing");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const bytes = await stampPdf(originalBytes.current, placements);
            const blob = new Blob([bytes], { type: "application/pdf" });
            await uploadSignedCopy(projectId, file.name, blob);
            setDone(true);
            if (onDone) await onDone();
            setTimeout(onClose, 1200);
        } catch (e) {
            console.error("Error saving signed pdf:", e);
            setError("upload");
            setBusy(false);
        }
    };

    return (
        <div className="sign-overlay" role="dialog" aria-modal="true">
            <div className="sign-panel">
                <div className="sign-header">
                    <strong><Trans>Sign document</Trans>: {file.name}</strong>
                    <button type="button" className="sign-close" onClick={onClose} disabled={busy} aria-label="close">
                        <FaTimes />
                    </button>
                </div>

                <div className="sign-tools">
                    <div className="sign-tools-row">
                        <label className="sign-label"><Trans>1. Draw your signature below</Trans></label>
                        <SignaturePad padRef={padRef} onChange={setHasSignature} />
                        <div className="sign-buttons">
                            <button type="button" className="sign-secondary" onClick={clearPad}><Trans>Clear</Trans></button>
                            <button type="button" className="sign-primary" onClick={armSignature} disabled={!hasSignature || !pdf}>
                                <Trans>Place signature</Trans>
                            </button>
                        </div>
                    </div>
                    <div className="sign-tools-row">
                        <label className="sign-label"><Trans>Text (name, date, hours...)</Trans></label>
                        <div className="sign-buttons">
                            <input
                                type="text"
                                className="sign-text-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") armText(); }}
                            />
                            <button type="button" className="sign-secondary" onClick={armText} disabled={!text.trim() || !pdf}>
                                <Trans>Place text</Trans>
                            </button>
                        </div>
                    </div>
                    <div className={`sign-hint${pending ? " sign-hint-active" : ""}`}>
                        {pending ? (
                            pending.kind === "signature"
                                ? <Trans>2. Tap on the document where the signature should go.</Trans>
                                : <Trans>2. Tap on the document where the text should go.</Trans>
                        ) : (
                            <Trans>You can drag a placed item; tap the cross to remove it.</Trans>
                        )}
                    </div>
                </div>

                <div className="sign-pages" ref={pagesRef}>
                    {loadError && <p className="signed-copy-error"><Trans>Could not load this document.</Trans></p>}
                    {!pdf && !loadError && <p><FaSpinner className="loading-icon" /> <Trans>Loading document...</Trans></p>}
                    {pdf && pageSizes.map((size, i) => (
                        <PdfPage
                            key={i}
                            pdf={pdf}
                            index={i}
                            width={pageWidth}
                            pageSize={size}
                            placements={placements.filter((p) => p.page === i)}
                            onPlace={place}
                            onMove={movePlacement}
                            onRemove={removePlacement}
                            armed={!!pending}
                        />
                    ))}
                </div>

                <div className="sign-footer">
                    {error === "nothing" && <span className="signed-copy-error"><Trans>Place at least one signature first.</Trans></span>}
                    {error === "upload" && <span className="signed-copy-error"><Trans>Upload failed, please try again.</Trans></span>}
                    {done && <span className="sign-done"><Trans>Signed document saved.</Trans></span>}
                    <button type="button" className="sign-secondary" onClick={onClose} disabled={busy}><Trans>Cancel</Trans></button>
                    <button type="button" className="sign-primary" onClick={send} disabled={busy || !pdf || done}>
                        {busy ? <FaSpinner className="loading-icon" /> : <Trans>3. Send signed document</Trans>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignPdfDialog;
